import { localDateKey } from "./todo";

export type CalendarSystem = "jalali" | "gregorian";
export type JalaliDateParts = { year: number; month: number; day: number };
export type CalendarDay = {
  date: Date;
  dateKey: string;
  inMonth: boolean;
  primaryDay: string;
  secondaryDay: string;
};

const jalaliPartsFormatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian-nu-latn", {
  year: "numeric", month: "numeric", day: "numeric"
});
const jalaliDayFormatter = new Intl.DateTimeFormat("fa-IR-u-ca-persian", { day: "numeric" });

function atNoon(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
}

export function addCalendarDays(value: Date, days: number): Date {
  const next = atNoon(value);
  next.setDate(next.getDate() + days);
  return next;
}

export function jalaliParts(value: Date): JalaliDateParts {
  const parts = jalaliPartsFormatter.formatToParts(value);
  const number = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: number("year"), month: number("month"), day: number("day") };
}

export function calendarMonthStart(anchor: Date, system: CalendarSystem): Date {
  if (system === "gregorian") return new Date(anchor.getFullYear(), anchor.getMonth(), 1, 12);
  let cursor = atNoon(anchor);
  for (let index = 0; index < 32; index++) {
    if (jalaliParts(cursor).day === 1) return cursor;
    cursor = addCalendarDays(cursor, -1);
  }
  return cursor;
}

export function moveCalendarMonth(anchor: Date, direction: -1 | 1, system: CalendarSystem): Date {
  if (system === "gregorian") return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1, 12);
  const start = calendarMonthStart(anchor, system);
  return direction === -1 ? calendarMonthStart(addCalendarDays(start, -1), system) : calendarMonthStart(addCalendarDays(start, 32), system);
}

export function buildCalendarDays(anchor: Date, system: CalendarSystem): CalendarDay[] {
  const monthStart = calendarMonthStart(anchor, system);
  const weekStart = system === "jalali" ? 6 : 0;
  const offset = (monthStart.getDay() - weekStart + 7) % 7;
  const gridStart = addCalendarDays(monthStart, -offset);
  const targetJalali = jalaliParts(monthStart);
  return Array.from({ length: 42 }, (_, index) => {
    const date = addCalendarDays(gridStart, index);
    const parts = jalaliParts(date);
    const inMonth = system === "jalali"
      ? parts.year === targetJalali.year && parts.month === targetJalali.month
      : date.getFullYear() === monthStart.getFullYear() && date.getMonth() === monthStart.getMonth();
    return {
      date,
      dateKey: localDateKey(date),
      inMonth,
      primaryDay: system === "jalali" ? jalaliDayFormatter.format(date) : String(date.getDate()),
      secondaryDay: system === "jalali" ? String(date.getDate()) : jalaliDayFormatter.format(date)
    };
  });
}

export function calendarMonthTitle(anchor: Date, system: CalendarSystem): { primary: string; secondary: string } {
  const start = calendarMonthStart(anchor, system);
  if (system === "jalali") {
    return {
      primary: new Intl.DateTimeFormat("fa-IR-u-ca-persian", { month: "long", year: "numeric" }).format(start),
      secondary: new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(start)
    };
  }
  return {
    primary: new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(start),
    secondary: new Intl.DateTimeFormat("fa-IR-u-ca-persian", { month: "long", year: "numeric" }).format(start)
  };
}

export function formatDualCalendarDate(value: Date): { jalali: string; gregorian: string } {
  return {
    jalali: new Intl.DateTimeFormat("fa-IR-u-ca-persian", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(value),
    gregorian: new Intl.DateTimeFormat("en-US", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(value)
  };
}
